import axios from 'axios';

import {
  currentPath,
  stampEbayAccountHeader,
  stampMetaAdAccountHeader,
  stampShopifyLinkHeader,
  stampWalmartAccountHeader,
} from '@/api/interceptors/channelAccountHeader.js';
import { gateGuard } from '@/api/interceptors/gateGuard.js';
import { hasScopeOverride, stampMarketplaceScope } from '@/api/interceptors/marketplaceScope.js';
import { tokenService } from '@/services/tokenService.js';
import { useAuthStore } from '@/stores/auth.js';
import { useLoaderStore } from '@/stores/loader.js';
import { deepSnakeToCamel, deepCamelToSnake } from '@/utils/caseConverter.js';

// Caller identifier sent on every wiseppc-ads-api request so the AI
// proposed-actions audit trail can record which surface created or
// commented on each row. Browsers FORBID JS from setting User-Agent, so we
// use a custom header instead. ads-api ``get_caller_client`` reads this
// header first, falls back to User-Agent for non-browser callers.
// The ``wiseppc-webapp/`` prefix is what makes ``detect_client_type``
// return ``"wiseppc_webapp"`` — the suffix is informational only.
const WISEPPC_CLIENT_HEADER = `wiseppc-webapp/${__APP_VERSION__}`;

const $axios = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosPublic = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosOlap = axios.create({
  baseURL: import.meta.env.VITE_API_OLAP_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosAmazonAds = axios.create({
  baseURL: import.meta.env.VITE_API_AMAZON_ADS_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Unset on stage/prod until the /app/api path is live there; the service answers the same routes under /ads/api.
const $axiosApp = axios.create({
  baseURL: import.meta.env.VITE_API_APP_URL || import.meta.env.VITE_API_AMAZON_ADS_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosMcp = axios.create({
  baseURL: import.meta.env.VITE_API_MCP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosBilling = axios.create({
  baseURL: import.meta.env.VITE_API_BILLING_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosInventory = axios.create({
  baseURL: import.meta.env.VITE_API_INVENTORY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const $axiosManager = axios.create({
  baseURL: import.meta.env.VITE_API_MANAGER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const handleRequestError = (error) => Promise.reject(error);

const handleResponseSuccess = (response) => {
  tokenService.resetFailureCount();
  return response;
};

// Public auth endpoints where a 401 is terminal (handled by the page), not refreshable.
const noRetryURLs = [
  '/auth/login',
  '/auth/register',
  '/auth/confirm',
  '/auth/resend-code',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/oauth/exchange',
  // Authenticated, but its 401 is user-facing ("Current password is incorrect"), not token
  // expiry. Retrying re-sends the same wrong old password (still 401) and needlessly burns a
  // refresh cycle — enough of which trip handleRefreshFailure into a forced logout. Terminal so
  // the interceptor flags it isUserFacingAuthError and setErrors surfaces the backend message.
  '/auth/change-password',
  // Terminal by design: logout proceeds locally whatever the backend says, and a retry would
  // re-enter the token-refresh path mid-logout (the deadlock fixed in session.getSessionToken).
  '/auth/logout',
  // Authenticated, but a 401/403 here means "this invite is not for you" (or is spent), not token
  // expiry. Refresh-and-retry only cascades into a forced logout of every tab. The caller
  // (pendingInvite) handles the failure and retries later.
  '/invites/accept',
];

const setAuthorizationHeader = async (config) => {
  // Abort non-auth (business-API) requests during auth transitions (logout / switch). Manager
  // requests are the auth flow itself, so they always proceed.
  if (config.__client !== 'manager') {
    const authStore = useAuthStore();
    if (authStore.isAuthLoading) {
      const err = new Error('Auth transition in progress — request aborted');
      err.code = 'ERR_AUTH_LOADING';
      return Promise.reject(err);
    }
  }

  let token = null;
  try {
    token = await tokenService.getTokenForRequest(config);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Don't mask the failure as a generic "no token" — surface it with the request URL so a real
    // mint/refresh bug is diagnosable instead of only showing up later as a noisy 401. Flow is
    // unchanged: the request still proceeds (token stays null) so the 401 → refresh → logout path
    // can recover when this is a genuine token failure for a resolvable BP.
    console.warn(`[axios] Token retrieval failed for ${config.url}:`, e);
  }

  // Don't fire a token-less resource request just because auth is still warming up. If there is no
  // token AND no business profile can be resolved yet (store + storage + URL all empty, e.g. during
  // restore before the first profile sync), defer the request instead of sending it without a JWT
  // (the "empty Authorization" noise the backend flagged). The caller re-fires once a token exists —
  // analytics grids and settings stores already watch authStore.accessToken. Excludes
  // /auth/* (mint/refresh), so the very requests that establish readiness aren't blocked.
  // If a bpId IS resolvable but the token is still missing (genuine mint/refresh failure), the
  // request is allowed through so the existing 401 → refresh → logout path can run.
  if (!token && config.__client !== 'manager') {
    const authStore = useAuthStore();
    if (authStore.isAuthenticated) {
      const resolvedBpId = await tokenService.resolveBusinessProfileId(config);
      if (!resolvedBpId) {
        const err = new Error('Auth not ready — no business profile resolved, request deferred');
        err.code = 'ERR_AUTH_LOADING';
        return Promise.reject(err);
      }
    }
  }

  // Resolve + stamp the businessProfileId header via the single canonical resolver (same logic
  // used for the token and the 401 handler) so the whole request lifecycle agrees on one BP.
  const businessProfileId = await tokenService.resolveBusinessProfileId(config);
  if (businessProfileId && !config.skipBusinessProfileId) {
    config.headers.businessProfileId = businessProfileId;
  }

  // Stamp the active marketplace's identifiers so the backend can route by whichever applies
  // (see stampMarketplaceScope). Respect a per-request override (hasScopeOverride): when
  // X-Profile-Id or X-Marketplace-Id is already set explicitly, leave the whole set alone so we
  // never mix the current marketplace's ids with an overridden one.
  if (!hasScopeOverride(config.headers)) {
    try {
      const { useMarketplacesAnalytics } = await import('@/composables/analytics/useMarketplacesAnalytics');
      const { currentMarketplace, account } = useMarketplacesAnalytics();
      stampMarketplaceScope(
        config.headers,
        currentMarketplace.value,
        account.currentBusinessProfile?.amazonSPAccounts,
      );
    } catch { /* ignore - not all contexts have marketplace */ }
  }

  return config;
};

const retryRequest = (config) => {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        await setAuthorizationHeader(config);
        // Выбираем правильный клиент исходя из тега __client или baseURL
        let client = $axios;
        if (config.__client === 'olap' || config.baseURL === import.meta.env.VITE_API_OLAP_URL) {
          client = $axiosOlap;
        } else if (config.__client === 'app') {
          // Tag only. The /ads/api fallback means both clients can share a baseURL.
          client = $axiosApp;
        } else if (
          config.__client === 'amazonAds' ||
          config.baseURL === import.meta.env.VITE_API_AMAZON_ADS_URL
        ) {
          client = $axiosAmazonAds;
        } else if (
          config.__client === 'mcp' ||
          config.baseURL === import.meta.env.VITE_API_MCP_API_URL
        ) {
          client = $axiosMcp;
        } else if (
          config.__client === 'billing' ||
          config.baseURL === import.meta.env.VITE_API_BILLING_URL
        ) {
          client = $axiosBilling;
        } else if (
          config.__client === 'inventory' ||
          config.baseURL === import.meta.env.VITE_API_INVENTORY_URL
        ) {
          client = $axiosInventory;
        } else if (
          config.__client === 'manager' ||
          config.baseURL === import.meta.env.VITE_API_MANAGER_URL
        ) {
          client = $axiosManager;
        }
        const response = await client(config);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    }, 300);
  });
};

const handleResponseError = async (error) => {
  const authStore = useAuthStore();
  const { response, config } = error;

  if (!config) return Promise.reject(error);

  if (config.url === '/auth/refresh') {
    // Per-BP app-token refresh: a rejected BP refresh token (stale wp.auth.bp.* entry) is
    // recoverable — tokenService falls back to minting via /auth/switch-profile. Only a
    // rejected SESSION refresh token means the session itself is dead.
    if (config.__bpTokenRefresh) {
      if (response?.status === 401) {
        console.warn('[auth] per-BP token refresh rejected (401) — falling back to /auth/switch-profile mint.');
      }
      return Promise.reject(error);
    }
    if (response?.status === 401) {
      // Loud by design: a rejected refresh token means the backend invalidated the session
      // (service restart / auth deploy). The forced logout below is the correct UX, but the
      // cause must stay visible in the console — otherwise a backend-side auth breakage
      // looks like users being randomly logged out.
      console.error(
        '[auth] /auth/refresh returned 401 — backend rejected the session refresh token, forcing logout.',
        response?.data ?? '',
      );
      await authStore.logout({ source: 'forced' });
      // The preloader is only hidden by the login page / a settled profile sync; a forced
      // logout mid-bootstrap must drop it explicitly or the app stays behind it forever.
      const loaderStore = useLoaderStore();
      loaderStore.hideLoader();
    }
    return Promise.reject(error);
  }

  const isAuthError = response?.status === 401;

  // __noSessionRefresh requests fire while their caller holds AUTH_REFRESH_LOCK_SESSION
  // (switch back to the default profile): the retry path below would call invalidateAndRefresh →
  // session.refreshSession → the same non-reentrant lock → origin-wide deadlock. Treat their 401
  // as terminal, like the noRetryURLs — the error is surfaced to the switch caller instead.
  if (!isAuthError || config.__noSessionRefresh || (config.url && noRetryURLs.includes(config.url))) {
    // A 401 on a public/terminal auth endpoint (login, register, confirm, forgot/reset…) is a
    // real user-facing error, not token-expiry noise — mark it so setErrors surfaces the message.
    if (isAuthError) error.isUserFacingAuthError = true;
    // Silent background token heal after 500 — keeps UX non-blocking
    if (response?.status === 500 && config.__client !== 'manager') {
      const bpId = await tokenService.resolveBusinessProfileId(config);
      if (bpId && authStore.internal.isExpiredFor(bpId)) {
        authStore.internal.refreshFor(bpId).catch(() => {
          console.warn('[axios] Silent background token refresh after 500 error failed.');
        });
      }
    }
    return Promise.reject(error);
  }

  // Allow only 1 retry per request to prevent infinite loops
  config.__retryCount = (config.__retryCount || 0) + 1;

  if (config.__retryCount > 1) {
    // Already retried once after a refresh — session appears persistently invalid
    try {
      await tokenService.handleRefreshFailure();
    } catch (logoutError) {
      const loaderStore = useLoaderStore();
      loaderStore.hideLoader();
      throw logoutError;
    }
    return Promise.reject(error);
  }

  // First 401 for this request — force a token refresh via tokenService (deduplicates concurrently)
  try {
    await tokenService.invalidateAndRefresh(config, await tokenService.resolveBusinessProfileId(config));
  } catch (refreshError) {
    console.error('[axios] Token refresh failed:', refreshError.message);
    // Lock-wait and request timeouts say nothing about the backend rejecting the tokens,
    // so neither must count toward the forced-logout threshold.
    if (['ERR_AUTH_LOCK_TIMEOUT', 'ECONNABORTED', 'ETIMEDOUT'].includes(refreshError?.code)) {
      return Promise.reject(refreshError);
    }
    try {
      await tokenService.handleRefreshFailure();
    } catch (logoutError) {
      const loaderStore = useLoaderStore();
      loaderStore.hideLoader();
      throw logoutError;
    }
    return Promise.reject(refreshError);
  }

  // Retry the request with the fresh token
  return retryRequest(config);
};

$axios.interceptors.request.use(async (config) => {
  config.__client = 'default';
  await gateGuard(config);
  return await setAuthorizationHeader(config);
}, handleRequestError);
$axios.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosPublic.interceptors.request.use(async (config) => {
  return await setAuthorizationHeader(config);
}, handleRequestError);
$axiosPublic.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosOlap.interceptors.request.use(async (config) => {
  config.__client = 'olap';
  // Capture an explicit per-request profile override (e.g. the marketplace KPI
  // fan-out queries each profile with its own X-Profile-Id; the Search Term Results grid sets
  // X-Marketplace-Id) BEFORE the header logic runs — otherwise the default below would clobber
  // it with the current marketplace and every request would resolve to the same profile.
  const hasProfileOverride = hasScopeOverride(config.headers);
  await gateGuard(config);
  const result = await setAuthorizationHeader(config);
  // Add Amazon Ads profile ID so the OLAP server can scope queries per marketplace
  if (!hasProfileOverride) {
    try {
      const { useAccountStore } = await import('@/stores/account.js');
      const profileId = useAccountStore().currentMarketplace?.profileId;
      if (profileId) {
        result.headers['X-Profile-Id'] = profileId;
      }
    } catch { /* ignore */ }
  }
  // eBay pages scope olap to the selected eBay account (channelAccountHeader.js).
  try {
    const { useEbayAccountStore } = await import('@/stores/ebayAccount.js');
    stampEbayAccountHeader(result, {
      path: currentPath(),
      accountId: useEbayAccountStore().selectedAccountId,
    });
  } catch { /* ignore */ }
  // Shopify pages scope olap to the selected Shopify store link (same helper module).
  try {
    const { useShopifyLinkStore } = await import('@/stores/shopifyLink.js');
    stampShopifyLinkHeader(result, {
      path: currentPath(),
      linkId: useShopifyLinkStore().selectedAccountId,
    });
  } catch { /* ignore */ }
  // Walmart pages scope olap to the selected Walmart account (same helper module).
  try {
    const { useWalmartAccountStore } = await import('@/stores/walmartAccount.js');
    stampWalmartAccountHeader(result, {
      path: currentPath(),
      accountId: useWalmartAccountStore().selectedAccountId,
    });
  } catch { /* ignore */ }
  // Meta pages scope olap to the selected Meta ad account (same helper module).
  try {
    const { useMetaAdAccountStore } = await import('@/stores/metaAdAccount.js');
    stampMetaAdAccountHeader(result, {
      path: currentPath(),
      adAccountId: useMetaAdAccountStore().selectedAccountId,
    });
  } catch { /* ignore */ }
  return result;
}, handleRequestError);
$axiosOlap.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosAmazonAds.interceptors.request.use(async (config) => {
  config.__client = 'amazonAds';
  await gateGuard(config);
  const result = await setAuthorizationHeader(config);
  // Identify ourselves to ads-api so AI proposed-actions audit rows
  // get created_by_client_type='wiseppc_webapp' / author_client_type='wiseppc_webapp'.
  // See WISEPPC_CLIENT_HEADER comment at the top of this file.
  result.headers['X-Wiseppc-Client'] = WISEPPC_CLIENT_HEADER;
  return result;
}, handleRequestError);
$axiosAmazonAds.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosApp.interceptors.request.use(async (config) => {
  config.__client = 'app';
  await gateGuard(config);
  const result = await setAuthorizationHeader(config);
  // Identify ourselves to ads-api so AI proposed-actions audit rows
  // get created_by_client_type='wiseppc_webapp' / author_client_type='wiseppc_webapp'.
  // See WISEPPC_CLIENT_HEADER comment at the top of this file.
  result.headers['X-Wiseppc-Client'] = WISEPPC_CLIENT_HEADER;
  return result;
}, handleRequestError);
$axiosApp.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosMcp.interceptors.request.use(async (config) => {
  config.__client = 'mcp';
  await gateGuard(config);
  return await setAuthorizationHeader(config);
}, handleRequestError);
$axiosMcp.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosBilling.interceptors.request.use(async (config) => {
  config.__client = 'billing';
  return await setAuthorizationHeader(config);
}, handleRequestError);
$axiosBilling.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosInventory.interceptors.request.use(async (config) => {
  config.__client = 'inventory';
  await gateGuard(config);
  return await setAuthorizationHeader(config);
}, handleRequestError);
$axiosInventory.interceptors.response.use(handleResponseSuccess, handleResponseError);
$axiosManager.interceptors.request.use(async (config) => {
  config.__client = 'manager';
  config.skipBusinessProfileId = true;
  await gateGuard(config);
  // setAuthorizationHeader runs first (attaches the Bearer token) before deepCamelToSnake
  // converts the body to snake_case in one pass.
  const configured = await setAuthorizationHeader(config);
  // `skipCaseConversion` must be honoured on BOTH sides. The /api-keys* surface carries the
  // permission catalog and the scope document, whose keys are identifiers, not field names:
  // `amazon_sp` round-trips to `amazon_s_p`, and identity-keyed maps
  // (`grants: {capability_id: {op_id: mode}}`, op ids like `update/title`) are destroyed.
  // Converting only one direction still ruins the payload, so both interceptors check the flag.
  if (configured.data != null && !configured.skipCaseConversion) {
    const body = typeof configured.data === 'string' ? JSON.parse(configured.data) : configured.data;
    configured.data = deepCamelToSnake(body);
  }
  return configured;
}, handleRequestError);
$axiosManager.interceptors.response.use((response) => {
  tokenService.resetFailureCount();
  if (response.data != null && !response.config?.skipCaseConversion) {
    response.data = deepSnakeToCamel(response.data);
  }
  return response;
}, handleResponseError);

export { $axios, $axiosPublic, $axiosOlap, $axiosAmazonAds, $axiosApp, $axiosMcp, $axiosBilling, $axiosInventory, $axiosManager };
